import Vento from 'vento';

import Address from './Address';
import PartialAddressInput from './PartialAddressInput';
import GlobalAddressInput from './GlobalAddressInput';
import AddressMap from './AddressMap';
import Checker from 'softlicense/dist/plain/Checker';
import Util from './Util';

/**
 * Main module class which handles the Address-Autocomplete and gets exposed to 'window.TWTAddressAutocomplete'. The
 * module is build by defining a global address-input and partial address-inputs. The global-input is enhanced by the
 * Google Places Autocomplete, the partial-inputs are enhanced so that the input address is geocoded and thus validated
 * upon entering. An optional Google-Map can be used to display the address visually on a map and offer the opportunity
 * to change the address by dragging the map-marker or clicking on the map.
 * This module makes use of the Google Maps API with the 'places' library. Thus it is necessary that the API is loaded
 * before loading this module.
 * It triggers the following Google API events:
 * - 'address_cleared': when the address-input was cleared because the global-input did change
 * - 'address_removed': when the address-input was completely removed (e.g. because the global-input was cleared or due
 *                      to a geocoding error)
 * - 'address_set': when the address was set; however there may still be address-parts missing (check partial-inputs)
 */
export default class TWTAddressAutocomplete extends Vento {
    /**
     * Does the license check, sets up the input-fields and their events and sets up other class members.
     * The complete autocomplete-GUI will be displayed by calling 'render' on the autocomplete-instance.
     * @param {string} license string containing the license-code for this module.
     * @param {Element} globalInput input-element for the global address-input.
     * @param {Object} partialInputs an object representing the partial-input elements; its properties are address-types
     * as defined at https://developers.google.com/maps/documentation/geocoding/intro#Types; the property-values are
     * input-elements which connect to these address-parts.
     */
    constructor(license, globalInput, partialInputs) {
        super();
        this.addEvent('initialized');
        this.addEvent('error', this.handleError);
        this.error = null;

        try {
            // do license check
            this.licenseValid = true;
            const checker = new Checker(
                '0kvyMGt2eWhqM2M3NzYrNzc2cDc3NjY3NzZ4Nzc2cjc3Nkk3NzZJNzc2Rzc3NkU3NzZaNzc2YTc3Nk03N'
                + 'ytHNzc2Vzc3NlU3NzZBNzc2Sjc3Nlo3NzZiNzc2Yzc3K1c3NzZXNzc2WDc3NkU3NzZHNzcrQTc3Nks3Nz'
                + 'ZCNzc2TTc3Nks3NzZJNzc2Qzc3KzI3NzZwNzc2bzc3NnA=hj3c', license);
            if (checker.isLicenseValid()) {
                // setup class members
                this.geolocationUrl = 'https://www.googleapis.com/geolocation/v1/geolocate?key=';
                this.map = null;
                this.mapOptions = null;
                this.address = null;
                this.geoCoder = new google.maps.Geocoder();
                this.userPosition = null;
                this.apiKey = null;
                this.doSilentAutoLocate = false;
                this.doHtml5AutoLocate = false;

                // setup partial-inputs
                this.partialInputs = [];
                for (let addressType in partialInputs) {
                    if (partialInputs.hasOwnProperty(addressType)) {
                        const inputElement = partialInputs[addressType];
                        if (!Util.isSet(inputElement)) {
                            this.error = new Error('AddressAutocomplete: invalid partial-input');
                            return;
                        }
                        // setup and store the partial-input for the given address-type
                        const partialInput = new PartialAddressInput(addressType, inputElement);
                        this.partialInputs.push(partialInput);
                        google.maps.event.addListener(partialInput, 'input_changed', this.findAddress.bind(this));
                    }
                }

                // setup global-input
                this.globalInput = null;
                if (Util.isSet(globalInput)) {
                    this.globalInput = new GlobalAddressInput(globalInput);
                    // bind events to global-input
                    google.maps.event.addListener(this.globalInput, 'address_entered', this.findAddress.bind(this));
                    google.maps.event.addListener(this.globalInput, 'address_changed', this.setAddress.bind(this));
                    google.maps.event.addListener(this.globalInput, 'input_changed', () => {
                        // clear all address-inputs
                        this.address = null;
                        if (Util.isSet(this.map)) {
                            this.map.clear();
                        }
                        for (const partialInputId in this.partialInputs) {
                            if (this.partialInputs.hasOwnProperty(partialInputId)) {
                                this.partialInputs[partialInputId].clear();
                            }
                        }
                        google.maps.event.trigger(this, 'address_cleared');
                    });
                } else {
                    this.error = new Error('globalInput element missing');
                    return;
                }
            } else {
                this.licenseValid = false;
                this.error = new Error('Invalid license for TWT Address Autocomplete specified');
                return;
            }
        } catch (e) {
            this.error = e;
            this.licenseValid = false;
            return;
        }
    }

    /**
     * Fired if some error occurred.
     * @param {Error} error the error occurred
     * @returns {null} null
     */
    handleError(error) {
        Util.warn(error);
        this.error = error;
    }

    /**
     * Sets the api-key used to silently auto-locate the user.
     * @param {string} apiKey google-maps api-key
     * @returns {null} null
     */
    setupSilentAutoLocate(apiKey) {
        if (!this.licenseValid) {
            return;
        }
        this.doSilentAutoLocate = true;
        this.apiKey = apiKey;
    }

    /**
     * Sets the flag to use html5-based user-localisation.
     * @returns {null} null
     */
    setupHtml5AutoLocate() {
        if (!this.licenseValid) {
            return;
        }
        this.doHtml5AutoLocate = true;
    }

    /**
     * Sets up an optional Google map to display the address on and offer the possibility to change it by clicking the
     * map or moving the map-marker.
     * @param {Element} canvas element to render the map in.
     * @param {google.maps.LatLng|google.maps.LatLngLiteral|null} position default position of the map.
     * @param {number|null} zoom default zoom-level of the map.
     * @returns {null} null
     */
    setupMap(canvas, position = null, zoom = null) {
        if (!this.licenseValid) {
            return;
        }
        // check map canvas
        if (!Util.isSet(canvas)) {
            return this.fireError('AddressAutocomplete: invalid map-canvas');
        }
        // setup default position if no position is given
        if (!Util.isLatLng(position)) {
            position = {lat:0, lng:0};
        }
        // build map-options
        this.mapOptions = {
            canvas: canvas,
            center: position,
            zoom: (zoom || 1)
        };
    }

    /**
     * Find an address by the given query where query is either an address, a string, a geo-location or empty. If the
     * query is empty, the query is build from the values of the partial-input elements. The Google maps geocoder is
     * used to find the address.
     * @param {Address|google.maps.LatLng|google.maps.LatLngLiteral|string|null} query the address query
     * @returns {null} null
     */
    findAddress(query = null) {
        if (!this.licenseValid) {
            return;
        }
        // build geo-coder request
        let request = null;
        if (!Util.isSet(query)) { // query is null; get address from input fields
            let address = new Address();
            for (const partialInputId in this.partialInputs) {
                if (this.partialInputs.hasOwnProperty(partialInputId)) {
                    const partialInput = this.partialInputs[partialInputId];
                    let addressType = partialInput.getType();
                    address[addressType] = partialInput.getValue();
                }
            }
            request = {address: address.toString()};
        } else if (query instanceof Address) { // query is address
            request = {address: query.toString()};
        } else if (query.hasOwnProperty('lat') && query.hasOwnProperty('lng')) { // query is geo-location
            request = {location: query};
        } else { // query is other
            request = {address: query};
        }
        // send geocoder request
        this.geoCoder.geocode(request, (results, status) => {
            let foundAddress = null;
            if (status === google.maps.GeocoderStatus.OK) {
                foundAddress = Address.fromGeoCoderResults(results);
                foundAddress.query = query;
            } else {
                Util.warn('AddressAutocomplete: Geocoder failed due to: ' + status);
            }
            this.setAddress(foundAddress);
        });
    }

    /**
     * Set the given address while address is either empty or an instance of Address.
     * @param {Address|null} address the actual address
     * @returns {null} null
     */
    setAddress(address = null) {
        if (!this.licenseValid) {
            return;
        }
        if (!Util.isSet(address) || address instanceof Address) {
            // set the address for all input elements
            this.address = address;
            if (Util.isSet(this.globalInput)) {
                this.globalInput.setAddress(address);
            }
            if (Util.isSet(this.map)) {
                this.map.setAddress(address);
            }
            // fill address-parts
            for (const partialInputId in this.partialInputs) {
                if (this.partialInputs.hasOwnProperty(partialInputId)) {
                    this.partialInputs[partialInputId].setAddress(address);
                }
            }
            if (!Util.isSet(address)) {
                google.maps.event.trigger(this, 'address_cleared');
                google.maps.event.trigger(this, 'address_removed');
            } else {
                google.maps.event.trigger(this, 'address_set');
            }
        }
    }

    /**
     * Checks the license and JS stack and executeds {#renderElements}.
     * @returns {null} null
     */
    render() {
        // push everything to the stack to avoid stuttering view
        setTimeout(() => {
            if (this.error) {
                this.fireError(this.error);
            } else if (this.licenseValid) {
                this.renderElements();
                this.fireInitialized();
            }
        }, 0);
    }

    /**
     * Completely renders the autocomplete-GUI and auto-locates the user if enabled.
     * @returns {null} null
     */
    renderElements() {
        // setup google map
        if (Util.isSet(this.mapOptions)) {
            this.map = new AddressMap(this.mapOptions.canvas, this.mapOptions.center, this.mapOptions.zoom);
            google.maps.event.addListener(this.map, 'input_changed', () => {
                // clear address inputs
                this.address = null;
                if (Util.isSet(this.globalInput)) {
                    this.globalInput.setAddress(null);
                }
                for (const partialInputId in this.partialInputs) {
                    if (this.partialInputs.hasOwnProperty(partialInputId)) {
                        this.partialInputs[partialInputId].clear();
                    }
                }
                google.maps.event.trigger(this, 'address_cleared');
            });
            google.maps.event.addListener(this.map, 'position_changed', this.findAddress.bind(this));
        }

        // try to locate the user
        if (!Util.isSet(this.address)) {
            // silent ip-based-localisation
            if (this.doSilentAutoLocate && Util.isSet(this.apiKey)) {
                let xhttp;
                if (window.XMLHttpRequest) { // modern browser
                    xhttp = new XMLHttpRequest();
                } else { // IE6, IE5
                    xhttp = new ActiveXObject('Microsoft.XMLHTTP');
                }
                /**
                 * Silent ip-based-localisation
                 * @returns {null} null
                 */
                xhttp.onreadystatechange = () => {
                    if (xhttp.readyState === 4 && xhttp.status === 200) {
                        // check if address was not already set
                        if (!Util.isSet(this.userPosition)) {
                            let userPosition = JSON.parse(xhttp.responseText);
                            this.setUserPosition(userPosition.location, userPosition.accuracy);
                        }
                    }
                };
                xhttp.open('POST', this.geolocationUrl + this.apiKey, true);
                xhttp.send();
            }
            // html5 localisation
            if (this.doHtml5AutoLocate && !Util.isSet(this.address) && Util.isSet(navigator.geolocation)) {
                navigator.geolocation.getCurrentPosition((userPosition) => {
                    // check if address was not already set
                    if (!Util.isSet(this.address)) {
                        // build the perimeter as a circle
                        let position = {lat: userPosition.coords.latitude, lng: userPosition.coords.longitude};
                        let accuracy = userPosition.coords.accuracy;
                        this.setUserPosition(position, accuracy);
                    }
                });
            }
        }
    }

    /**
     * Set the current user-position.
     * @param {google.maps.LatLng|google.maps.LatLngLiteral} position geo-coordinates
     * @param {int} accuracy position accuracy in meters
     * @returns {null} null
     */
    setUserPosition(position, accuracy) {
        this.userPosition = position;
        let perimeter = new google.maps.Circle({
            center: position,
            radius: accuracy
        });
        // set the perimeter bounds for the places autocomplete
        let bounds = perimeter.getBounds();
        if (Util.isSet(this.globalInput)) {
            this.globalInput.setBounds(bounds);
        }
        // show the perimeter on the map
        if (Util.isSet(this.map)) {
            this.map.setPerimeter(perimeter);
        }
    }
}
// expose the module main class
window.TWTAddressAutocomplete = TWTAddressAutocomplete;